import { useEffect, useState } from "react";

// Match/group payloads (schedules/stages, group-stage/groups, the grid/
// bracket endpoint) carry participant names and IDs but never a photo URL -
// that only exists on GET /api/v1.0/tournaments/{id}/players/ (the same
// endpoint TournamentPlayers.js already reads via SmoothAvatar). Building a
// { [player_id]: image } map here once per tournament lets match rows join
// their own player_1/2/3/4 IDs against it, instead of the backend needing
// to inline images into every match/group response.
//
// This cache used to have NO invalidation at all - once a tournament's map
// was fetched, every match/group/schedule modal kept serving that same
// snapshot for the rest of the page session. Add a photo to a participant
// from the Участники tab, then open Жеребкування/Групи/Розклад: that
// participant's avatar slot would show "no photo" (or a stale old photo)
// forever, since nothing ever re-fetched or even knew the map was stale.
// Looked exactly like a caching bug because it was one.
//
// invalidateTournamentPlayerImage lets whoever just learned a player's real
// `image` (TournamentPlayers.js, right after adding/editing a participant)
// patch every mounted instance of this hook for that tournament immediately,
// without forcing a full re-fetch of the whole map.
const imagesCache = {};
const listeners = {};

export const invalidateTournamentPlayerImage = (tournamentId, playerId, image) => {
    if (!tournamentId || !playerId) return;
    const next = { ...(imagesCache[tournamentId] || {}), [playerId]: image || null };
    imagesCache[tournamentId] = next;
    (listeners[tournamentId] || []).forEach(setImages => setImages(next));
};

export default function useTournamentPlayerImages(api, tournamentId, enabled) {
    const [images, setImages] = useState(() => imagesCache[tournamentId] || {});

    useEffect(() => {
        if (!tournamentId) return;
        if (!listeners[tournamentId]) listeners[tournamentId] = new Set();
        listeners[tournamentId].add(setImages);
        return () => listeners[tournamentId].delete(setImages);
    }, [tournamentId]);

    useEffect(() => {
        if (!enabled || !tournamentId) return;
        if (imagesCache[tournamentId]) {
            setImages(imagesCache[tournamentId]);
            return;
        }

        let cancelled = false;
        const fetchAll = async () => {
            let url = `/api/v1.0/tournaments/${tournamentId}/players/?limit=200`;
            const map = {};
            try {
                while (url) {
                    const res = await api.get(url);
                    (res.data.results || []).forEach(p => {
                        if (p.player_id) map[p.player_id] = p.image || null;
                    });
                    url = res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, "") : null;
                }
                if (cancelled) return;
                imagesCache[tournamentId] = map;
                setImages(map);
            } catch (e) {
                console.error(e);
            }
        };
        fetchAll();
        return () => { cancelled = true; };
    }, [api, tournamentId, enabled]);

    return images;
}
